import { createStore } from '@stencil/store';
import { InternalChatState } from 'types';
import { getPreviousChat, getVoiceMode } from 'utils/utils';
import { triggerHotJarEvent } from 'utils/events';
import { changeOverflowBody, forceFocusChatTextarea, registerAutoScrollHandler } from 'utils/ui';
import { hasMessageSentFromUser } from 'utils/messages';
import { resetDistributionModal } from './distributionModal';
import initParams from './initParams';

const initialFeedback = JSON.parse(
  JSON.stringify({
    messageId: null,
    type: null,
    text: '',
  }),
);

const { state, onChange, on, set, reset } = createStore<InternalChatState>({
  voiceMode: getVoiceMode(),
  inputMode: 'TEXT', // TEXT | VOICE
  // TODO: Eliminar todo lo relacionado a este parametro una vez este confirmado que no hay que restaurar el modal
  finishedInitialTour: true,
  preventLastMessageAnimations: false,
  readonly: false,
  showFullScreenModal: null,
  state: 'LOADING',
  feedback: initialFeedback,
  feedbackForm: {
    score: -1,
    question1: '',
    sent: false,
  },
  viewMoreModal: {
    title: '',
    body: '',
  },
  previousChat: getPreviousChat(),
  messages: [],
  filters: [],
  favorites: new Map(),
});

on('set', (key, newValue, oldValue) => {
  if (key === 'state' && newValue === 'CONVERSATION' && oldValue === 'LOADING') {
    //initInitialTour();
    registerAutoScrollHandler(initParams.mode);
  }

  if (key === 'showFullScreenModal') {
    if (newValue === null) {
      if (oldValue === 'FEEDBACK') {
        requestAnimationFrame(() => (state.feedback = initialFeedback));
      }

      if (oldValue === 'DISTRIBUTION') {
        requestAnimationFrame(() => resetDistributionModal());
      }
    }

    changeOverflowBody(newValue !== null, initParams.isApp);
  }

  if (key === 'messages') {
    // Check if we're transitioning from PROGRESS to RESPONSE in the last message
    const oldLastMessage = oldValue?.[oldValue.length - 1];
    const newLastMessage = newValue?.[newValue.length - 1];

    const isProgressToResponseTransition = oldLastMessage?.variant === 'PROGRESS' && (newLastMessage?.variant === 'RESPONSE' || !newLastMessage?.variant);

    // Allow animations when:
    // 1. Messages array grows (normal case), OR
    // 2. Last message transitions from PROGRESS to RESPONSE (our filtering case)
    state.preventLastMessageAnimations = newValue?.length <= oldValue?.length && !isProgressToResponseTransition;
  }
});

onChange('messages', newValue => {
  const hasAnyMessageSentFromUser = hasMessageSentFromUser(newValue);

  // Update filters with the latest assistance message
  const lastMessage = newValue?.[newValue.length - 1];
  if (lastMessage?.isAssistant) {
    state.filters = newValue[newValue.length - 1].context?.filters || [];
  }

  // Record if have two boards of products
  const hasTwoBoards = newValue.filter(board => board.context?.category === 'PRODUCT').length === 2;
  if (hasTwoBoards) {
    triggerHotJarEvent('two_boards_products');
  }
});

onChange('state', newValue => {
  if (newValue === 'CONVERSATION') {
    forceFocusChatTextarea(initParams.isMobile);
  }
});

onChange('userInfo', newValue => {
  if (typeof newValue?.externalUserId === 'string' && newValue.externalUserId.trim() !== '') {
    console.debug('externalUserId changed to: ', newValue.externalUserId);
    sessionStorage.setItem('external-user-id', newValue.externalUserId);
  }

  if (typeof newValue?.temporalUserId === 'string' && newValue.temporalUserId.trim() !== '') {
    console.debug('temporalUserId changed to: ', newValue.temporalUserId);
    localStorage.setItem('temporal-user-id', newValue.temporalUserId);
  }
});

window.document.addEventListener('reset-chat-stores', () => {
  reset();
});

export default state;
